import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Modal, Field } from '@/features/transactions/TransactionForm';

/**
 * Troca e redefinição de senha — RF-AUT.
 *
 * Dois modos no mesmo diálogo porque a diferença entre eles é uma só, e é
 * importante deixá-la visível: trocar a própria senha exige a senha atual;
 * redefinir a de outra pessoa não exige, e por isso vira linha de auditoria.
 */
export function PasswordDialog({
  mode,
  targetName,
  targetId,
  onClose,
  onDone,
}: {
  mode: 'self' | 'admin';
  targetName?: string;
  targetId?: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      mode === 'self'
        ? api.post<{ message: string }>('/auth/password', {
            current_password: current,
            password,
            password_confirmation: confirmation,
          })
        : api.post<{ message: string }>(`/users/${targetId}/password`, {
            password,
            password_confirmation: confirmation,
          }),
    onSuccess: (res) => {
      alert(res.message);
      onDone();
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        // A mensagem de campo é mais útil que a genérica: "Senha atual
        // incorreta" diz o que fazer; "Dados inválidos" não diz nada.
        const first = Object.values(e.errors ?? {}).flat()[0];
        setError(first ?? e.message);
        return;
      }
      setError('Não foi possível alterar a senha.');
    },
  });

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirmation.length > 0 && password !== confirmation;

  return (
    <Modal
      title={mode === 'self' ? 'Trocar minha senha' : `Redefinir senha de ${targetName}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        className="space-y-3.5"
      >
        {mode === 'admin' && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Você está definindo a senha de outra pessoa. A ação fica registrada na auditoria com o
            seu nome — trocar a senha de alguém é poder assumir a identidade dela.
            <br />
            <strong>Informe a nova senha por um canal seguro</strong>, nunca por e-mail ou mensagem
            em texto.
          </p>
        )}

        {mode === 'self' && (
          <Field label="Senha atual" required>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="input w-full"
            />
          </Field>
        )}

        <Field
          label="Nova senha"
          required
          hint="Mínimo 12 caracteres, com maiúsculas, minúsculas e números."
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            autoComplete="new-password"
            className="input w-full"
          />
        </Field>

        {tooShort && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Faltam {12 - password.length} caractere(s).
          </p>
        )}

        <Field label="Repita a nova senha" required>
          <input
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            required
            autoComplete="new-password"
            className="input w-full"
          />
        </Field>

        {mismatch && (
          <p className="text-xs text-rose-600 dark:text-rose-400">As duas senhas não coincidem.</p>
        )}

        {/* O sistema recusa senha que já apareceu em vazamento público. A
            verificação usa k-anonimato: só cinco caracteres do hash saem daqui,
            nunca a senha. Dizer isso evita que a recusa pareça capricho. */}
        <p className="text-xs text-slate-400">
          Senhas que já apareceram em vazamentos públicos são recusadas. A checagem envia apenas um
          trecho do código da senha, nunca a senha em si.
        </p>

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending || tooShort || mismatch || password === ''}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Alterar senha'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
